import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError, money, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProductForm } from "@/pages/admin/ProductForm";
import { StatusPill } from "@/pages/Account";

const STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];

const Stat = ({ label, value, testid }) => (
  <div className="border border-line bg-white p-5">
    <p className="eyebrow">{label}</p>
    <p data-testid={testid} className="mt-3 font-display text-4xl">{value}</p>
  </div>
);

const ProductsTable = ({ products, onEdit, onDelete }) => (
  <div className="overflow-x-auto border border-line bg-white">
    <table className="w-full min-w-[720px] text-left text-sm" data-testid="admin-products-table">
      <thead className="border-b border-line font-mono text-[11px] uppercase tracking-widest text-stone">
        <tr><th className="p-4">Garment</th><th className="p-4">Category</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4" /></tr>
      </thead>
      <tbody>
        {products.map((p) => (
          <tr key={p.product_id} className="border-b border-line last:border-0" data-testid={`admin-product-row-${p.product_id}`}>
            <td className="flex items-center gap-3 p-4"><img src={imgSrc(p.images?.[0])} alt="" className="h-12 w-10 object-cover" /><span className="font-medium">{p.name}</span>{p.featured && <span className="h-2 w-2 bg-signal" title="Featured" />}</td>
            <td className="p-4 capitalize">{p.category}</td>
            <td className="p-4 font-mono">{money(p.price)}</td>
            <td className="p-4 font-mono text-xs">{Object.entries(p.stock || {}).map(([s, n]) => `${s}:${n}`).join("  ")}</td>
            <td className="p-4 text-right">
              <button onClick={() => onEdit(p)} data-testid={`admin-edit-${p.product_id}`} className="p-2 hover:text-signal" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => onDelete(p)} data-testid={`admin-delete-${p.product_id}`} className="p-2 hover:text-signal" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TrackingInput = ({ order, onSave }) => {
  const [val, setVal] = useState(order.tracking_number || "");
  const dirty = val !== (order.tracking_number || "");
  return (
    <div className="mt-2 flex items-center gap-1">
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Tracking №"
        data-testid={`tracking-input-${order.order_id}`}
        className="w-28 border border-line bg-bone px-2 py-1 font-mono text-[11px] outline-none focus:border-ink"
      />
      {dirty && (
        <button onClick={() => onSave(order, val)} data-testid={`tracking-save-${order.order_id}`} className="text-[11px] link-underline whitespace-nowrap">Save</button>
      )}
    </div>
  );
};

const OrdersTable = ({ orders, onStatus, onTracking }) => (
  <div className="overflow-x-auto border border-line bg-white">
    <table className="w-full min-w-[1080px] text-left text-sm" data-testid="admin-orders-table">
      <thead className="border-b border-line font-mono text-[11px] uppercase tracking-widest text-stone">
        <tr><th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Deliver to</th><th className="p-4">Items</th><th className="p-4">Total</th><th className="p-4">Payment</th><th className="p-4">Status</th></tr>
      </thead>
      <tbody>
        {orders.length === 0 && <tr><td colSpan={7} className="p-6 text-stone">No orders yet.</td></tr>}
        {orders.map((o) => {
          const a = o.shipping_address || {};
          const addr = [a.line1, a.line2, a.city, a.postal_code, a.country].filter(Boolean).join(", ");
          return (
            <tr key={o.order_id} className="border-b border-line last:border-0" data-testid={`admin-order-row-${o.order_id}`}>
              <td className="p-4 font-mono text-xs">{o.order_id}<br /><span className="text-stone">{new Date(o.created_at).toLocaleString("en-GB")}</span></td>
              <td className="p-4" data-testid={`admin-order-customer-${o.order_id}`}>
                {o.customer_name && <span className="block font-medium">{o.customer_name}</span>}
                <span className="text-stone">{o.email}</span>
                {!o.user_id && <span className="ml-2 font-mono text-[10px] text-stone">GUEST</span>}
              </td>
              <td className="p-4 text-xs" data-testid={`admin-order-address-${o.order_id}`}>{addr || <span className="text-stone">{o.payment_status === "paid" ? "Fetching…" : "—"}</span>}{a.phone && <><br /><span className="text-stone">{a.phone}</span></>}</td>
              <td className="p-4 text-xs">{(o.items || []).map((i) => `${i.name} (${i.size}) ×${i.quantity}`).join(", ")}</td>
              <td className="p-4 font-mono">{money(o.total)}</td>
              <td className="p-4"><StatusPill status={o.payment_status === "paid" ? "paid" : "pending"} testid={`admin-order-payment-${o.order_id}`} /></td>
              <td className="p-4">
                <select value={o.status} onChange={(e) => onStatus(o, e.target.value)} data-testid={`admin-order-status-${o.order_id}`} className="border border-line bg-bone px-2 py-1 text-xs">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
                <TrackingInput order={o} onSave={onTracking} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const PromosTab = ({ promos, onCreate, onDeactivate }) => {
  const [form, setForm] = useState({ code: "", kind: "percent", value: "", max_redemptions: "", valid_days: "" });
  const [saving, setSaving] = useState(false);
  const input = "w-full border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink";
  const save = async () => {
    setSaving(true);
    try {
      const ok = await onCreate({ ...form, value: parseFloat(form.value), max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null, valid_days: form.valid_days ? parseInt(form.valid_days) : null });
      if (ok) setForm({ code: "", kind: form.kind, value: "", max_redemptions: "", valid_days: "" });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="grid gap-6 md:grid-cols-12">
      <div className="border border-line bg-white p-5 md:col-span-5" data-testid="promo-create-card">
        <p className="eyebrow">New promo code</p>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs">Code<input placeholder="LAUNCH10" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={input} data-testid="promo-code-input" /></label>
            <label className="block text-xs">Type
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={input} data-testid="promo-kind-input">
                <option value="percent">% off</option><option value="fixed">£ off</option>
              </select>
            </label>
          </div>
          <label className="block text-xs">{form.kind === "percent" ? "Percent off (1-90)" : "Amount off (£)"}
            <input type="number" min="1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={input} data-testid="promo-value-input" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs">Max uses (optional)<input type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} className={input} /></label>
            <label className="block text-xs">Valid for days (optional)<input type="number" min="1" value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })} className={input} /></label>
          </div>
          <button onClick={save} disabled={saving || !form.code || !form.value} data-testid="promo-save-button" className="w-full rounded-full bg-ink py-3 text-sm text-bone hover:bg-signal disabled:opacity-60">{saving ? "Creating…" : "Create promo code"}</button>
        </div>
      </div>
      <div className="overflow-x-auto border border-line bg-white md:col-span-7">
        <table className="w-full min-w-[480px] text-left text-sm" data-testid="admin-promos-table">
          <thead className="border-b border-line font-mono text-[11px] uppercase tracking-widest text-stone">
            <tr><th className="p-4">Code</th><th className="p-4">Discount</th><th className="p-4">Used</th><th className="p-4">Status</th><th className="p-4" /></tr>
          </thead>
          <tbody>
            {promos.length === 0 && <tr><td colSpan={5} className="p-6 text-stone">No promo codes yet.</td></tr>}
            {promos.map((p) => (
              <tr key={p.id} className="border-b border-line last:border-0" data-testid={`promo-row-${p.code}`}>
                <td className="p-4 font-mono">{p.code}</td>
                <td className="p-4 font-mono">{p.discount}</td>
                <td className="p-4 font-mono text-xs">{p.times_redeemed}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""}</td>
                <td className="p-4"><StatusPill status={p.active ? "paid" : "cancelled"} testid={`promo-status-${p.code}`} /></td>
                <td className="p-4 text-right">{p.active && <button onClick={() => onDeactivate(p)} data-testid={`promo-deactivate-${p.code}`} className="text-xs link-underline">Deactivate</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, o, s, pr] = await Promise.all([api.get("/admin/products"), api.get("/admin/orders"), api.get("/admin/stats"), api.get("/admin/promos")]);
      setProducts(Array.isArray(p.data) ? p.data : []);
      setOrders(Array.isArray(o.data) ? o.data : []);
      setStats(s.data || null);
      setPromos(Array.isArray(pr.data) ? pr.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user === null) return <main className="min-h-screen" />;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/admin/products/${p.product_id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (o, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${o.order_id}`, { status });
      toast.success(data?.email_sent ? `Order ${o.order_id} → ${status}. Shipping email sent.` : `Order ${o.order_id} → ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const createPromo = async (form) => {
    try {
      await api.post("/admin/promos", form);
      toast.success(`Promo ${form.code} created`);
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };
  const deactivatePromo = async (p) => {
    try { await api.post(`/admin/promos/${p.id}/deactivate`); toast.success(`${p.code} deactivated`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveTracking = async (o, tracking_number) => {
    try {
      await api.patch(`/admin/orders/${o.order_id}`, { tracking_number });
      toast.success(`Tracking number saved for ${o.order_id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `goencho-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Orders spreadsheet downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const tabCls = (t) => `rounded-full px-5 py-2 text-sm transition-colors ${tab === t ? "bg-ink text-bone" : "border border-line hover:border-ink"}`;

  return (
    <main data-testid="admin-page" className="mx-auto max-w-[1600px] px-5 pb-32 pt-28 md:px-10 md:pt-32">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <p className="eyebrow">Studio — Admin</p>
          <h1 className="mt-3 font-display text-6xl font-light leading-none">Back <em>room.</em></h1>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="w-fit rounded-full border border-ink px-5 py-2 text-sm hover:bg-ink hover:text-bone">Sign out</button>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Garments" value={stats?.products ?? 0} testid="stat-products" />
        <Stat label="Orders" value={stats?.orders ?? 0} testid="stat-orders" />
        <Stat label="Paid orders" value={stats?.paid_orders ?? 0} testid="stat-paid" />
        <Stat label="Revenue" value={money(stats?.revenue ?? 0)} testid="stat-revenue" />
      </div>
      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          <button className={tabCls("products")} onClick={() => setTab("products")} data-testid="admin-tab-products">Products</button>
          <button className={tabCls("orders")} onClick={() => setTab("orders")} data-testid="admin-tab-orders">Orders</button>
          <button className={tabCls("promos")} onClick={() => setTab("promos")} data-testid="admin-tab-promos">Promos</button>
        </div>
        {tab === "orders" && (
          <button onClick={exportCsv} data-testid="export-csv-button" className="rounded-full border border-ink px-5 py-2 text-sm transition-colors hover:bg-ink hover:text-bone">Export CSV</button>
        )}
        {tab === "products" && (
          <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="admin-add-product-button" className="flex items-center gap-2 rounded-full bg-ink px-5 py-2 text-sm text-bone hover:bg-signal"><Plus className="h-4 w-4" />Add garment</button>
        )}
      </div>
      <div className="mt-6">
        {tab === "products"
          ? <ProductsTable products={products} onEdit={(p) => { setEditing(p); setFormOpen(true); }} onDelete={del} />
          : tab === "orders"
            ? <OrdersTable orders={orders} onStatus={setStatus} onTracking={saveTracking} />
            : <PromosTab promos={promos} onCreate={createPromo} onDeactivate={deactivatePromo} />}
      </div>
      <ProductForm open={formOpen} onOpenChange={setFormOpen} product={editing} onSaved={load} />
    </main>
  );
}
